import React from 'react';
import PIENotification, { IPieNotificationMode } from '../PIENotification';
import styles from './PIENotificationReporter.module.scss';

import { useTranslation } from 'react-i18next';
import { Heading } from 'snacks-design-system';
import { INotificationAPI, useNotificationReadMutation } from '@src/shared/queries';
import { useQueryClient } from 'react-query';
import EmptyPageNotifiers, { EEmptyPageNotifierTypes } from '../EmptyPageNotifiers';
import PIEText from '../PIEText';

export enum NotificationDrawerStatuses {
  OrderDelivered = 'ORDER_DELIVERED',
  OrderInDelivery = 'ORDER_IN_DELIVERY',
  CourierAcceptedInvitation = 'COURIER_ACCEPTED_INVITATION',
  CourierReturning = 'COURIER_RETURNING_TO_RESTAURANT',
}

export const PIEDrawerNotificationReporter = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const notificationsQueryCache = queryClient.getQueryCache().find('notifications')?.state
    .data as INotificationAPI[];

  const markNotificationAsRead = useNotificationReadMutation({
    onSuccess: () => {
      queryClient.invalidateQueries('notifications');
    },
    onError: () => {
      queryClient.invalidateQueries('notifications');
    },
    onSettled: () => {},
  });

  const handleOnNotificationClose = (notificationId: any) => {
    return markNotificationAsRead.mutate([notificationId]);
  };

  const handleOnClearAllNotifications = () => {
    const notificationIds = notificationsQueryCache.map((notification) => notification.id);
    return markNotificationAsRead.mutate(notificationIds);
  };

  const notificationParser = (data: INotificationAPI) => {
    const courierName = (data && data?.extraParameters?.courierName) || '';
    const address = data?.extraParameters?.address || '';
    const notificationTypes = {
      [NotificationDrawerStatuses.OrderDelivered]: IPieNotificationMode.Positive,
      [NotificationDrawerStatuses.OrderInDelivery]: IPieNotificationMode.Info,
      [NotificationDrawerStatuses.CourierReturning]: IPieNotificationMode.Info,
      [NotificationDrawerStatuses.CourierAcceptedInvitation]: IPieNotificationMode.Positive,
    };

    const notificationTranslationParserTitle = {
      [NotificationDrawerStatuses.OrderDelivered]: t('notification_headers.orderDeliveredHeader'),
      [NotificationDrawerStatuses.OrderInDelivery]: t('notification_headers.orderInDeliveryHeader'),
      [NotificationDrawerStatuses.CourierReturning]: t('notification_headers.courierReturnHeader'),
      [NotificationDrawerStatuses.CourierAcceptedInvitation]: t(
        'notification_headers.connectionAcceptSuccessHeader'
      ),
    };

    const notificationTranslationParserMessage = {
      [NotificationDrawerStatuses.OrderDelivered]: t('graphql.SUCCESS_ORDER_DELIVERED', {
        Courier_name: courierName,
        address: address,
      }),
      [NotificationDrawerStatuses.OrderInDelivery]: t('graphql.SUCCESS_ORDER_INDELIVERY', {
        address: address,
      }),
      [NotificationDrawerStatuses.CourierReturning]: t('graphql.SUCCESS_COURIER_RETURN', {
        Courier_Name: courierName,
      }),
      [NotificationDrawerStatuses.CourierAcceptedInvitation]: t('notification.acceptSuccess', {
        email: courierName,
      }),
    };

    return (
      <PIENotification
        key={data.id}
        showIcon={true}
        modInline={true}
        isOpen={true}
        mode={notificationTypes[data.actionType]}
        showClose={true}
        onClose={() => handleOnNotificationClose(data.id)}
      >
        <Heading level={6}>{notificationTranslationParserTitle[data.actionType]}</Heading>

        <PIEText
          size={`var(--xds-font-size-16)`}
          text={notificationTranslationParserMessage[data.actionType]}
        ></PIEText>
      </PIENotification>
    );
  };

  return (
    <div className={styles.container}>
      {(notificationsQueryCache?.length && (
        <a href="#" onClick={() => handleOnClearAllNotifications()}>
          Clear all notifications
        </a>
      )) || <></>}
      {notificationsQueryCache && notificationsQueryCache?.length ? (
        notificationsQueryCache
          .sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          })
          .map((notifications) => notificationParser(notifications))
      ) : (
        <EmptyPageNotifiers notifierType={EEmptyPageNotifierTypes.EmptyNotification} />
      )}
    </div>
  );
};

export default PIEDrawerNotificationReporter;
